
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Notification from "../Others/Notification";
import "./RegisterToNetwork.css";

const EVENT_ID = "networking-2026";

const EVENTS_API_URL =
    "https://script.google.com/macros/s/AKfycbwoNQHZnU5j4Pb8NIwzuVsBg5TBKlKRhYJXsxmCK0J6Rb8QRCf6-ixVudn7-KReaDcu/exec";

const EMAIL_API_URL = "https://amra-amra.se/emailApi/";

const careerOptions = [
    "Läkare",
    "Sjuksköterska",
    "Undersköterska",
    "Lärare",
    "Förskollärare",
    "Systemutvecklare",
    "IT-konsult",
    "Elektriker",
    "Ekonom",
    "Jurist",
    "Apotekare",
    "Servitör/Servitris",
    "Butikssäljare",
    "Taxiförare",
    "Bussförare",
    "Personlig assistent",
    "Social Handlägare",
    "Resturang Chef",
    "Others"
];

const participationGroups = [
    {
        title: "A. Seeking Support & Opportunities",
        options: [
            "Career guidance, job opportunities & career development",
            "Education, study information & professional advice",
            "Networking, mentorship & business connections"
        ]
    },
    {
        title: "B. Sharing Knowledge & Supporting Others",
        options: [
            "Sharing professional experience, knowledge & insights",
            "Mentoring, inspiring and helping others grow"
        ]
    }
];

function RegisterToNetwork() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [eventData, setEventData] = useState(null);

    const [selected, setSelected] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const [modalShow, setModalShow] = useState(false);
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");

    /* ==========================================
       LOAD EVENT SETTINGS
    ========================================== */

    useEffect(() => {
        const controller = new AbortController();

        async function loadEvent() {
            try {
                setLoading(true);
                setError("");

                const response = await fetch(
                    `${EVENTS_API_URL}?id=${encodeURIComponent(EVENT_ID)}`,
                    { signal: controller.signal }
                );

                if (!response.ok) {
                    throw new Error("Unable to load event information.");
                }

                const result = await response.json();

                if (!result.success || !result.event) {
                    throw new Error("Event information was not found.");
                }

                setEventData(result.event);
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.error(err);
                    setError(err.message);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        }

        loadEvent();

        return () => controller.abort();
    }, []);

    /* ==========================================
       REGISTRATION STATUS
    ========================================== */

    const getRegistrationStatus = () => {
        if (!eventData) return "loading";

        const {
            registration_open,
            registration_close,
            registration_api_link
        } = eventData;

        if (
            !registration_open ||
            !registration_close ||
            !registration_api_link
        ) {
            return "unavailable";
        }

        const openDate = new Date(
            `${registration_open}T00:00:00`
        );

        const closeDate = new Date(
            `${registration_close}T23:59:59`
        );

        if (
            Number.isNaN(openDate.getTime()) ||
            Number.isNaN(closeDate.getTime())
        ) {
            return "unavailable";
        }

        const now = new Date();

        if (now < openDate) return "not-open";
        if (now > closeDate) return "closed";

        return "open";
    };

    const registrationStatus = getRegistrationStatus();

    const formatDate = (value) => {
        if (!value) return "";

        const date = new Date(`${value}T00:00:00`);

        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric"
        });
    };

    /* ==========================================
       NOTIFICATION
    ========================================== */

    const showNotification = (heading, text) => {
        setTitle(heading);
        setMessage(String(text));
        setModalShow(true);
    };

    /* ==========================================
       EMAIL CONFIRMATION
    ========================================== */

    const sendEmail = async (formData) => {
        const emailData = new FormData();

        for (const [key, value] of formData.entries()) {
            emailData.append(key, value);
        }

        emailData.append(
            "request",
            "networkRegistrationEmail"
        );

        try {
            const response = await axios.post(
                EMAIL_API_URL,
                emailData
            );

            showNotification(
                "Registration Completed",
                response.data || "Your registration was successful."
            );
        } catch (err) {
            console.error("Email error:", err);

            showNotification(
                "Registration Completed",
                "Your registration was saved, but we could not send the confirmation email."
            );
        }
    };

    /* ==========================================
       SUBMIT REGISTRATION
    ========================================== */

    const Submit = async (e) => {
        e.preventDefault();

        if (submitting || registrationStatus !== "open") {
            return;
        }

        const formElm = e.currentTarget;
        const formData = new FormData(formElm);

        /* Collect selected participation goals */

        const selectedGoals = formData.getAll("ParticipationGoals");

        formData.delete("ParticipationGoals");

        formData.append(
            "ParticipationGoals",
            selectedGoals.join(" | ")
        );

        /* Tell backend which event is registering */

        formData.append(
            "event_id",
            EVENT_ID
        );


        /* Event identifier */

        formData.append("event_id", EVENT_ID);

        try {
            setSubmitting(true);

            const response = await axios.post(
                eventData.registration_api_link,
                formData
            );

            if (
                Array.isArray(response.data) &&
                response.data[0] === "successful"
            ) {
                formElm.reset();
                setSelected("");

                await sendEmail(formData);
            } else {
                const responseMessage = Array.isArray(response.data)
                    ? response.data.join(", ")
                    : typeof response.data === "string"
                        ? response.data
                        : JSON.stringify(response.data);

                showNotification(
                    "Registration Unsuccessful",
                    responseMessage || "Unable to complete registration."
                );
            }
        } catch (err) {
            console.error("Registration error:", err);

            showNotification(
                "Failed to Register",
                err.message || "Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    /* ==========================================
       REUSABLE FORM SECTION HEADING
    ========================================== */

    const SectionHeading = ({ number, heading, description }) => (
        <div className="network-form-section-title">
            <span>{number}</span>

            <div>
                <h3>{heading}</h3>
                <p>{description}</p>
            </div>
        </div>
    );

    /* ==========================================
       PAGE
    ========================================== */

    return (
        <main className="network-registration-page">

            {/* BREADCRUMB */}

            <div className="container network-registration-breadcrumb">
                <Link to="/">Home</Link>
                <span>›</span>
                <Link to="/events">Events</Link>
                <span>›</span>
                <span>Registration</span>
            </div>

            {/* HEADER */}

            <section className="network-registration-header">
                <div className="container">
                    <div className="network-registration-header-content">

                        <span className="network-registration-label">
                            Event Registration
                        </span>

                        <h1>
                            {eventData?.title_en || "Career Forum 2026"}
                        </h1>

                        <h2>
                            Networking for Bangladeshis in Sweden
                        </h2>

                        <p>
                            Complete the form below to register for the event.
                        </p>

                    </div>
                </div>
            </section>

            {/* REGISTRATION */}

            <section className="network-registration-section">
                <div className="container">
                    <div className="network-registration-card">

                        <div className="network-form-heading">
                            <div className="network-form-heading-icon">
                                ✓
                            </div>

                            <div>
                                <h2>Registration Form</h2>
                                <p>Please provide your information below.</p>
                            </div>
                        </div>

                        {/* LOADING */}

                        {loading && (
                            <div className="network-form-message">
                                <div className="network-loading-spinner" />
                                <p>Please wait while loading the form...</p>
                            </div>
                        )}

                        {/* ERROR */}

                        {!loading && error && (
                            <div className="network-form-alert network-form-alert-error">
                                <strong>Unable to load registration</strong>
                                <p>{error}</p>
                            </div>
                        )}

                        {/* NOT OPEN */}

                        {!loading && !error &&
                            registrationStatus === "not-open" && (
                                <div className="network-form-alert network-form-alert-warning">
                                    <strong>Registration Not Open Yet</strong>
                                    <p>
                                        Registration will open on{" "}
                                        {formatDate(eventData.registration_open)}.
                                    </p>
                                </div>
                            )}

                        {/* CLOSED */}

                        {!loading && !error &&
                            registrationStatus === "closed" && (
                                <div className="network-form-alert network-form-alert-warning">
                                    <strong>Registration Closed</strong>
                                    <p>
                                        Registration closed on{" "}
                                        {formatDate(eventData.registration_close)}.
                                    </p>
                                </div>
                            )}

                        {/* UNAVAILABLE */}

                        {!loading && !error &&
                            registrationStatus === "unavailable" && (
                                <div className="network-form-alert network-form-alert-warning">
                                    <strong>Registration Unavailable</strong>
                                    <p>
                                        Registration settings are not available.
                                        Please contact us for more information.
                                    </p>
                                </div>
                            )}

                        {/* FORM */}

                        {!loading && !error &&
                            registrationStatus === "open" && (

                                <form
                                    id="networkForm"
                                    className="network-registration-form"
                                    onSubmit={Submit}
                                >

                                    {/* ==========================
                      01 PARTICIPATION GOALS
                  ========================== */}

                                    <div className="network-form-section">

                                        <SectionHeading
                                            number="01"
                                            heading="Participation Goals"
                                            description="What would you like to gain from or contribute to this event? Select all that apply."
                                        />

                                        {participationGroups.map((group) => (
                                            <div
                                                className="network-goals-group"
                                                key={group.title}
                                            >
                                                <h4>{group.title}</h4>

                                                <div className="network-goals-options">
                                                    {group.options.map((option) => (
                                                        <label
                                                            className="network-goal-option"
                                                            key={option}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                name="ParticipationGoals"
                                                                value={option}
                                                            />

                                                            <span>{option}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}

                                    </div>

                                    {/* ==========================
                      02 PERSONAL INFORMATION
                  ========================== */}

                                    <div className="network-form-section">

                                        <SectionHeading
                                            number="02"
                                            heading="Personal Information"
                                            description="Tell us a little about yourself."
                                        />

                                        <div className="network-form-grid">

                                            {/* NAME */}

                                            <div className="network-field">
                                                <label htmlFor="network-name">
                                                    Full Name <span>*</span>
                                                </label>

                                                <input
                                                    id="network-name"
                                                    name="Name"
                                                    type="text"
                                                    placeholder="Enter your full name"
                                                    required
                                                />
                                            </div>

                                            {/* EMAIL */}

                                            <div className="network-field">
                                                <label htmlFor="network-email">
                                                    Email Address <span>*</span>
                                                </label>

                                                <input
                                                    id="network-email"
                                                    name="Email"
                                                    type="email"
                                                    placeholder="name@example.com"
                                                    required
                                                />
                                            </div>

                                            {/* PHONE */}

                                            <div className="network-field network-field-full">
                                                <label htmlFor="network-phone">
                                                    Phone Number <span>*</span>
                                                </label>

                                                <input
                                                    id="network-phone"
                                                    name="Phone"
                                                    type="tel"
                                                    placeholder="Enter your phone number"
                                                    required
                                                />
                                            </div>

                                            {/* GENDER */}

                                            <div className="network-field network-field-full">
                                                <label>
                                                    Gender <span>*</span>
                                                </label>

                                                <div className="network-choice-group">

                                                    <label className="network-choice">
                                                        <input
                                                            type="radio"
                                                            name="Gender"
                                                            value="Male"
                                                            required
                                                        />
                                                        <span>Male</span>
                                                    </label>

                                                    <label className="network-choice">
                                                        <input
                                                            type="radio"
                                                            name="Gender"
                                                            value="Female"
                                                            required
                                                        />
                                                        <span>Female</span>
                                                    </label>

                                                </div>
                                            </div>

                                            {/* AGE */}

                                            <div className="network-field network-field-full">
                                                <label>
                                                    Age Group <span>*</span>
                                                </label>

                                                <div className="network-choice-group network-age-group">

                                                    {[
                                                        ["18-25", "18–25"],
                                                        ["26-35", "26–35"],
                                                        ["36-45", "36–45"],
                                                        ["45+", "45+"]
                                                    ].map(([value, label]) => (
                                                        <label
                                                            className="network-choice"
                                                            key={value}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="Age"
                                                                value={value}
                                                                required
                                                            />
                                                            <span>{label}</span>
                                                        </label>
                                                    ))}

                                                </div>
                                            </div>

                                        </div>
                                    </div>

                                    {/* ==========================
                                     03 EDUCATION & CAREER
                                    ========================== */}

                                    <div className="network-form-section">

                                        <SectionHeading
                                            number="03"
                                            heading="Education & Career"
                                            description="Help us understand your professional background."
                                        />

                                        <div className="network-form-grid">

                                            {/* EDUCATION */}

                                            <div className="network-field">
                                                <label htmlFor="network-education">
                                                    Highest Level of Education <span>*</span>
                                                </label>

                                                <input
                                                    id="network-education"
                                                    name="Education"
                                                    type="text"
                                                    placeholder="Your highest education"
                                                    required
                                                />
                                            </div>

                                            {/* OCCUPATION */}

                                            <div className="network-field network-field-full">
                                                <label htmlFor="network-occupation">
                                                    Current Occupation
                                                </label>

                                                <input
                                                    id="network-occupation"
                                                    name="Occupation"
                                                    type="text"
                                                    placeholder="Your current occupation"
                                                />
                                            </div>

                                            {/* CAREER INTEREST */}

                                            <div className="network-field network-field-full">
                                                <label htmlFor="network-subject">
                                                    Career Interest
                                                </label>

                                                <select
                                                    id="network-subject"
                                                    name="Subject"
                                                    value={selected}
                                                    onChange={(e) =>
                                                        setSelected(e.target.value)
                                                    }
                                                >
                                                    <option value="">
                                                        Select your career interest
                                                    </option>

                                                    {careerOptions.map((option) => (
                                                        <option
                                                            key={option}
                                                            value={option}
                                                        >
                                                            {option}
                                                        </option>
                                                    ))}
                                                </select>

                                                {selected === "Others" && (
                                                    <input
                                                        name="ExtSubject"
                                                        type="text"
                                                        className="network-other-input"
                                                        placeholder="Please specify your career interest"
                                                        required
                                                    />
                                                )}
                                            </div>

                                        </div>
                                    </div>

                                    {/* ==========================
                      SUBMIT
                  ========================== */}

                                    <div className="network-form-submit">

                                        <button
                                            type="submit"
                                            id="register"
                                            className="network-register-button"
                                            disabled={submitting}
                                        >
                                            <span>
                                                {submitting
                                                    ? "Registering..."
                                                    : "Register Now"}
                                            </span>

                                            <span>→</span>
                                        </button>

                                        <p>
                                            By registering, you confirm that the
                                            information provided above is correct.
                                        </p>

                                    </div>

                                </form>
                            )}

                        {/* NOTIFICATION */}

                        <Notification
                            show={modalShow}
                            onHide={() => setModalShow(false)}
                            title={title}
                            message={message}
                        />

                    </div>
                </div>
            </section>

        </main>
    );
}

export default RegisterToNetwork;
